/**
 * KITS — as variações da mensagem, por produto.
 *
 * Esta é a peça que faltava para qualquer campanha sair. `POST /campanhas/:id/
 * aprovar` recusa com 409 abaixo de 5 variações ativas, e não havia tela para
 * criá-las: a API existia desde sempre e nenhum caminho chegava nela.
 *
 * POR QUE CINCO, E POR QUE ISSO NÃO É BUROCRACIA
 *
 * O mesmo texto chegando a milhares de empresas é o que caracteriza massa. Os
 * dois números que esta operação perdeu caíram em disparo frio, e o segundo
 * **com a cadência já corrigida** — a conclusão registrada foi que a cadência
 * não resolve, porque o problema é a natureza da mensagem. A variação é o
 * controle primário; ritmo e distribuição são secundários.
 *
 * O servidor compara os textos NORMALIZADOS — sem acento, sem pontuação, sem
 * caixa. Trocar uma vírgula não faz variação nova, e ele recusa dizendo isso.
 *
 * MÍDIA FICA DE FORA, POR ORA
 *
 * O schema aceita mídia, mas exige `storageKey` e `hashArquivo` — e não há
 * armazenamento de objetos neste projeto. Oferecer o campo sem ter onde
 * guardar o arquivo seria a mesma armadilha da política de descanso: a tela
 * afirmando uma capacidade que não existe. Kit de texto já libera o disparo.
 */
import { useCallback, useMemo, useState } from "react";
import { api } from "../api.js";
import { irPara } from "../Casca.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  Selo,
  classeEntrada,
} from "../ui/base.js";
import { Conteudo, Gaveta, Grade, Indicadores, useCarga } from "../ui/comercial.js";

/** O mesmo mínimo que o servidor aplica. Repetido aqui só para avisar antes. */
const MINIMO_DE_VARIACOES = 5;

interface Kit {
  id: string;
  produtoId: string;
  produtoNome: string;
  rotulo: string;
  cta: string;
  variacoes: Array<{ id: string; ordem: number; corpo: string }>;
  midias: Array<{ id: string; tipo: string; rotulo: string }>;
  mensagensPorContato: number;
  nivelMidiaExigido: string;
}

const CTA: Record<string, string> = {
  nenhum: "Sem chamada — a resposta é o objetivo",
  numero_em_texto: "Número no texto — o contato disca",
  link_wa: "Link wa.me — montado pelo sistema",
};

/** Mesma normalização do servidor: caixa, acento e pontuação não contam. */
function normalizar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

export function Kits(): JSX.Element {
  const { estado, recarregar } = useCarga(
    useCallback(
      async () => ({
        kits: (await api.kits()) as { itens: Kit[] },
        produtos: await api.produtos(),
      }),
      [],
    ),
  );
  const [criando, setCriando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);

  return (
    <div className="grid gap-4">
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(d) => {
          const kits = d.kits.itens ?? [];
          const semKit = d.produtos.itens.filter(
            (p) => p.ativo && !kits.some((k) => k.produtoId === p.id),
          );
          const magros = kits.filter((k) => k.variacoes.length < MINIMO_DE_VARIACOES);

          return (
            <div className="grid gap-4">
              <Indicadores>
                <Indicador
                  rotulo="Kits"
                  valor={kits.length}
                  nota={`${d.produtos.itens.filter((p) => p.ativo).length} produto(s) ativo(s)`}
                />
                <Indicador
                  rotulo="Prontos para aprovar"
                  valor={kits.filter((k) => k.variacoes.length >= MINIMO_DE_VARIACOES).length}
                  nota={`com ${MINIMO_DE_VARIACOES} variações ou mais`}
                />
                <Indicador
                  rotulo="Produtos sem kit"
                  valor={semKit.length}
                  nota="A campanha deles nasce sem variação"
                />
              </Indicadores>

              {recado && <Aviso tom="ok">{recado}</Aviso>}

              {kits.length === 0 && (
                <Aviso tom="crit">
                  <div className="grid gap-2.5">
                    <span>
                      <strong>Nenhum kit cadastrado — e sem kit nenhuma campanha
                      pode ser aprovada.</strong>{" "}
                      A aprovação exige {MINIMO_DE_VARIACOES} variações ativas, e é
                      aqui que elas nascem. Não é burocracia: os dois números
                      perdidos nesta operação caíram em disparo frio, o segundo já
                      com a cadência corrigida. É a mensagem que queima chip.
                    </span>
                    {d.produtos.itens.length === 0 && (
                      <div>
                        <Botao tipo="principal" pequeno onClick={() => irPara("campanhas/produtos")}>
                          Cadastrar um produto antes
                        </Botao>
                      </div>
                    )}
                  </div>
                </Aviso>
              )}

              {magros.length > 0 && (
                <Aviso tom="warn">
                  {magros.length} kit(s) abaixo de {MINIMO_DE_VARIACOES} variações:{" "}
                  {magros.map((k) => k.produtoNome).join(", ")}. A campanha desses
                  produtos vai ser recusada na aprovação.
                </Aviso>
              )}

              <Painel
                titulo="Kits por produto"
                dica="Montado uma vez, reutilizado em toda campanha daquele produto"
                acoes={
                  <Botao
                    tipo="principal"
                    pequeno
                    desabilitado={semKit.length === 0}
                    {...(semKit.length === 0
                      ? { titulo: "Todo produto ativo já tem kit" }
                      : {})}
                    onClick={() => setCriando(true)}
                  >
                    Novo kit
                  </Botao>
                }
              >
                {kits.length === 0 ? (
                  <p className="text-[13px] text-tinta-3">
                    Nenhum kit ainda.
                  </p>
                ) : (
                  <div className="grid gap-3">
                    {kits.map((k) => (
                      <div key={k.id} className="rounded-[10px] border border-linha p-4">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <strong className="text-[13.5px]">{k.produtoNome}</strong>
                          <span className="text-[12px] text-tinta-3">{k.rotulo}</span>
                          <Selo
                            tom={k.variacoes.length >= MINIMO_DE_VARIACOES ? "ok" : "warn"}
                          >
                            {k.variacoes.length} variações
                          </Selo>
                          <span className="text-[12px] text-tinta-3">
                            {CTA[k.cta] ?? k.cta} · {k.mensagensPorContato} msg/contato
                          </span>
                        </div>
                        <ul className="mt-2.5 grid gap-1.5">
                          {k.variacoes.map((v) => (
                            <li
                              key={v.id}
                              className="text-[12.5px] text-tinta-2 whitespace-pre-line border-l-2 border-linha pl-3"
                            >
                              {v.corpo}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </Painel>

              <Painel titulo="O que o servidor recusa" dica="Vale conhecer antes de escrever">
                <ul className="grid gap-1.5 text-[13px] text-tinta-2">
                  <li>
                    <strong>Menos de {MINIMO_DE_VARIACOES} variações.</strong> O
                    mesmo texto em escala é o que caracteriza massa.
                  </li>
                  <li>
                    <strong>Variação que só muda pontuação.</strong> A comparação é
                    sobre o texto normalizado — sem acento, sem caixa, sem
                    pontuação. Trocar uma vírgula não é peça nova.
                  </li>
                  <li>
                    <strong>Nível de mídia incoerente.</strong> Exigir imagem sem ter
                    imagem derruba números aptos sem entregar nada em troca.
                  </li>
                </ul>
              </Painel>
            </div>
          );
        }}
      </Conteudo>

      {criando && estado.fase === "pronto" && (
        <FormularioKit
          produtos={estado.dados.produtos.itens.filter(
            (p) =>
              p.ativo &&
              !(estado.dados.kits.itens ?? []).some((k) => k.produtoId === p.id),
          )}
          aoFechar={() => setCriando(false)}
          aoSalvar={(m) => {
            setCriando(false);
            setRecado(m);
            recarregar();
          }}
        />
      )}
    </div>
  );
}

function FormularioKit({
  produtos,
  aoFechar,
  aoSalvar,
}: {
  produtos: Array<{ id: string; nome: string }>;
  aoFechar: () => void;
  aoSalvar: (motivo: string) => void;
}): JSX.Element {
  const [produtoId, setProdutoId] = useState(produtos[0]?.id ?? "");
  // A UNIQUE do banco é (empresa, produto, rótulo): o schema prevê mais de um
  // kit por produto. "Padrão" cobre o caso comum sem obrigar a inventar nome.
  const [rotulo, setRotulo] = useState("Padrão");
  const [cta, setCta] = useState("nenhum");
  // Cinco campos desde o começo: a tela mostra o alvo em vez de deixar a
  // pessoa escrever um, salvar, e descobrir na aprovação que faltavam quatro.
  const [corpos, setCorpos] = useState<string[]>(["", "", "", "", ""]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const preenchidas = corpos.map((c) => c.trim()).filter((c) => c.length >= 20);

  // A mesma checagem de duplicata que o servidor faz, para avisar ANTES de
  // salvar — o servidor continua sendo o gate; isto é só cortesia.
  const duplicadas = useMemo(() => {
    const vistos = new Map<string, number>();
    const pares: string[] = [];
    corpos.forEach((c, i) => {
      const t = c.trim();
      if (t.length < 20) return;
      const chave = normalizar(t);
      const antes = vistos.get(chave);
      if (antes !== undefined) pares.push(`${i + 1} repete a ${antes + 1}`);
      else vistos.set(chave, i);
    });
    return pares;
  }, [corpos]);

  const problema =
    produtoId === ""
      ? "Escolha o produto."
      : preenchidas.length < MINIMO_DE_VARIACOES
        ? `Faltam ${MINIMO_DE_VARIACOES - preenchidas.length} variação(ões) com ao menos 20 caracteres.`
        : duplicadas.length > 0
          ? `Variações repetidas: ${duplicadas.join("; ")}.`
          : null;

  async function salvar(): Promise<void> {
    if (problema) {
      setErro(problema);
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      const r = (await api.criarKit({
        produtoId,
        rotulo: rotulo.trim() || "Padrão",
        cta,
        variacoes: preenchidas.map((corpo, i) => ({ ordem: i + 1, corpo })),
        midias: [],
        mensagensPorContato: 1,
        nivelMidiaExigido: "texto",
      })) as { motivo?: string; queixas?: Array<{ motivo: string }> };
      aoSalvar(r.motivo ?? "Kit criado.");
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "O servidor recusou o kit.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Gaveta
      titulo="Novo kit"
      dica={`${MINIMO_DE_VARIACOES} variações do mesmo convite, escritas de jeitos diferentes`}
      largura="larga"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao
            tipo="principal"
            desabilitado={ocupado || problema !== null}
            onClick={() => void salvar()}
          >
            {ocupado ? "Salvando…" : "Salvar kit"}
          </Botao>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          {problema && (
            <span className="text-[12px] text-tinta-3 ml-auto max-w-[44ch] leading-relaxed">
              {problema}
            </span>
          )}
        </>
      }
    >
      <div className="grid gap-4">
        {erro && <Aviso tom="crit">{erro}</Aviso>}

        <Grade colunas={2}>
          <Campo rotulo="Produto" dica="Um kit por produto; serve a todas as campanhas dele">
            <select
              className={classeEntrada}
              value={produtoId}
              onChange={(e) => setProdutoId(e.target.value)}
            >
              {produtos.length === 0 && <option value="">Nenhum produto sem kit</option>}
              {produtos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo="Rótulo do kit" dica="Distingue kits do mesmo produto">
            <input
              className={classeEntrada}
              value={rotulo}
              placeholder="Padrão"
              onChange={(e) => setRotulo(e.target.value)}
            />
          </Campo>

          <Campo rotulo="Chamada para ação">
            <select
              className={classeEntrada}
              value={cta}
              onChange={(e) => setCta(e.target.value)}
            >
              {Object.entries(CTA).map(([id, rotulo]) => (
                <option key={id} value={id}>
                  {rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </Grade>

        <Aviso tom="warn">
          <strong>Escreva cinco convites diferentes, não cinco versões do mesmo.</strong>{" "}
          A comparação do servidor ignora acento, caixa e pontuação — trocar
          vírgula por ponto não cria variação. Mude a abertura, o comprimento e a
          ordem das ideias. É a mensagem, não a cadência, que queima chip.
        </Aviso>

        <div className="grid gap-3">
          {corpos.map((c, i) => {
            const t = c.trim();
            const curta = t.length > 0 && t.length < 20;
            return (
              <Campo
                key={i}
                rotulo={`Variação ${i + 1}`}
                dica={
                  t.length === 0
                    ? "mínimo 20 caracteres"
                    : `${t.length} caracteres${curta ? " — curta demais" : ""}`
                }
              >
                <textarea
                  className={`${classeEntrada} min-h-[84px] leading-relaxed`}
                  value={c}
                  placeholder={
                    i === 0
                      ? "Bom dia! Sou da CO Telecom, representante Vivo Empresas. Faço uma análise rápida da conta da sua empresa — costuma dar economia. Posso mandar os números?"
                      : ""
                  }
                  onChange={(e) => {
                    const novo = [...corpos];
                    novo[i] = e.target.value;
                    setCorpos(novo);
                  }}
                />
              </Campo>
            );
          })}
        </div>

        <div>
          <Botao tipo="fantasma" pequeno onClick={() => setCorpos([...corpos, ""])}>
            Acrescentar variação
          </Botao>
          <span className="ml-3 text-[12px] text-tinta-3">
            {preenchidas.length} de {MINIMO_DE_VARIACOES} prontas
          </span>
        </div>

        <Aviso tom="cool">
          <strong>Mídia não entra aqui por enquanto.</strong> O schema aceita, mas
          exige a chave e o hash do arquivo, e não há armazenamento de objetos
          neste projeto. Oferecer o campo sem ter onde guardar seria a tela
          afirmando uma capacidade que não existe. Kit de texto já libera o
          disparo.
        </Aviso>
      </div>
    </Gaveta>
  );
}
